import React from "react";
import { NavLink, Navigate, Routes, Route, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ADMIN } from "../constants/testIds";
import {
    Users, GraduationCap, Receipt, Megaphone, Calendar as CalendarIcon, Sparkles, Plus,
    DollarSign, School, Trash2, LayoutDashboard, LogOut, ChevronRight, Shuffle,
    HandCoins, Search, UserCog, Pencil, MessageSquare, ClipboardCheck, Crown, History
} from "lucide-react";
import ReassignBoard from "./ReassignBoard";
import MessagesPage from "./MessagesPage";
import AdminTeachersDirectory from "./AdminTeachersDirectory";
import AdminTeacherAttendance from "./AdminTeacherAttendance";
import { UsersAndRoles } from "./SuperAdminConsole";
import { AttachmentPicker, AttachmentList } from "../components/Attachments";
import { AddParentDialog, AddStudentDialog, ClassAssignSelect } from "../components/StudentAdminTools";
import { PaymentReminders } from "../components/PaymentReminders";
import UnpaidReport from "./UnpaidReport";
import { AlertTriangle } from "lucide-react";

const GRADES = ["PreK", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Quran", "Arabic"];

export default function AdminDashboard() {
    const { user: u } = useAuth();
    return (
        <div className="min-h-[calc(100vh-5rem)] bg-secondary/40">
            <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-0">
                <Sidebar />
                <div className="col-span-12 md:col-span-9 lg:col-span-10 p-6 lg:p-10">
                    <Routes>
                        <Route index element={<Overview />} />
                        <Route path="classes" element={<ClassesTab />} />
                        <Route path="reassign" element={<ReassignBoard />} />
                        <Route path="users" element={<UsersTab />} />
                        <Route path="parents" element={<ParentsDirectoryTab />} />
                        <Route path="students" element={<StudentsDirectoryTab />} />
                        <Route path="teachers" element={<AdminTeachersDirectory />} />
                        <Route path="teacher-attendance" element={<AdminTeacherAttendance />} />
                        <Route path="payments" element={<PaymentsTab />} />
                        <Route path="unpaid" element={<UnpaidReport />} />
                        <Route path="announcements" element={<AnnouncementsTab />} />
                        <Route path="events" element={<EventsTab />} />
                        <Route path="messages" element={<MessagesPage />} />
                        <Route path="roles" element={<UsersAndRoles selfId={u?.id} isSuper={u?.role === "superadmin"} />} />
                        <Route path="*" element={<Navigate to="/admin" replace />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
}

function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useI18n();
    const items = [
        { to: "/admin", label: t("admin_overview"), icon: LayoutDashboard, end: true },
        { to: "/admin/classes", label: t("admin_classes"), icon: GraduationCap, testid: ADMIN.classesTab },
        { to: "/admin/reassign", label: t("admin_reassign"), icon: Shuffle, testid: "admin-reassign-tab" },
        { to: "/admin/users", label: t("admin_users"), icon: Users, testid: ADMIN.usersTab },
        { to: "/admin/roles", label: t("admin_roles"), icon: UserCog, testid: "admin-roles-tab" },
        { to: "/admin/parents", label: t("admin_parents_dir"), icon: UserCog, testid: "admin-parents-tab" },
        { to: "/admin/students", label: t("admin_students_dir"), icon: GraduationCap, testid: "admin-students-tab" },
        { to: "/admin/teachers", label: t("admin_teachers_dir"), icon: UserCog, testid: "admin-teachers-dir-tab" },
        { to: "/admin/teacher-attendance", label: t("admin_teacher_attendance"), icon: ClipboardCheck, testid: "admin-teacher-attendance-tab" },
        { to: "/admin/payments", label: t("admin_payments"), icon: Receipt, testid: ADMIN.paymentsTab },
        { to: "/admin/unpaid", label: "Who hasn't paid", icon: AlertTriangle, testid: "admin-unpaid-tab" },
        { to: "/admin/messages", label: t("admin_messages"), icon: MessageSquare, testid: "admin-messages-tab" },
        { to: "/admin/announcements", label: t("admin_announcements"), icon: Megaphone, testid: ADMIN.announcementsTab },
        { to: "/admin/events", label: t("admin_events"), icon: CalendarIcon, testid: ADMIN.eventsTab },
    ];
    if (user?.role === "superadmin") {
        items.push({ to: "/superadmin", label: t("admin_superadmin_link"), icon: Crown, testid: "superadmin-console-link", external: true });
    }
    return (
        <aside className="col-span-12 md:col-span-3 lg:col-span-2 bg-primary text-primary-foreground md:min-h-[calc(100vh-5rem)] p-6 md:sticky md:top-20 md:h-[calc(100vh-5rem)] flex flex-col">
            <div>
                <div className="overline opacity-70 mb-2">{t("admin_portal_kicker")}</div>
                <div className="font-serif text-xl">{t("admin_control_center")}</div>
                <div className="text-xs opacity-70 mt-1">{user?.full_name}</div>
            </div>
            <nav className="mt-8 flex flex-col gap-1 flex-1 overflow-y-auto pr-1">
                {items.map(it => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.end}
                        data-testid={it.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/15 text-primary-foreground" : "text-primary-foreground/75 hover:bg-white/10"}`
                        }
                    >
                        <it.icon className="w-4 h-4" /> {it.label}
                    </NavLink>
                ))}
            </nav>
            <button
                onClick={() => { logout(); navigate("/"); }}
                className="mt-6 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-primary-foreground/75 hover:bg-white/10">
                <LogOut className="w-4 h-4" /> {t("nav_signout")}
            </button>
        </aside>
    );
}

function Overview() {
    const { user } = useAuth();
    const [stats, setStats] = React.useState(null);
    const [recentRegs, setRecentRegs] = React.useState([]);
    React.useEffect(() => {
        api.get("/admin/stats").then(r => setStats(r.data)).catch(() => { });
        api.get("/admin/registrations").then(r => setRecentRegs(r.data.slice(0, 5))).catch(() => { });
    }, []);
    return (
        <div>
            <div className="mb-8">
                <div className="overline text-brand-stone">Overview</div>
                <h1 className="font-serif text-4xl text-brand-ink">As-salamu alaykum, {user?.full_name?.split(" ")[0]}</h1>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid={ADMIN.statsCard}>
                <StatCard icon={Users} label="Students" value={stats?.students ?? "—"} />
                <StatCard icon={School} label="Families" value={stats?.families ?? "—"} />
                <StatCard icon={GraduationCap} label="Classes" value={stats?.classes ?? "—"} />
                <StatCard icon={DollarSign} label="Revenue" value={stats ? `$${stats.revenue_usd.toFixed(0)}` : "—"} accent />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="overline text-brand-stone mb-3">Quick stats</div>
                        <ul className="space-y-2 text-sm">
                            <li className="flex justify-between"><span className="text-brand-stone">Teachers</span><span className="font-dash font-semibold">{stats?.teachers ?? "—"}</span></li>
                            <li className="flex justify-between"><span className="text-brand-stone">Paid registrations</span><span className="font-dash font-semibold">{stats?.paid_registrations ?? "—"}</span></li>
                            <li className="flex justify-between"><span className="text-brand-stone">Pending registrations</span><span className="font-dash font-semibold">{stats?.pending_registrations ?? "—"}</span></li>
                        </ul>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div className="overline text-brand-stone">Recent registrations</div>
                            <NavLink to="/admin/payments" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                View all <ChevronRight className="w-3 h-3" />
                            </NavLink>
                        </div>
                        <ul className="space-y-2 text-sm">
                            {recentRegs.map(r => (
                                <li key={r.id} className="flex justify-between gap-2">
                                    <span className="text-brand-ink truncate">{r.parent_name}</span>
                                    <Badge className={`${r.status === "PAID" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0`}>{r.status}</Badge>
                                </li>
                            ))}
                            {recentRegs.length === 0 && <li className="text-brand-stone">No registrations yet.</li>}
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent }) {
    return (
        <Card className={accent ? "border-accent/40 bg-accent/5" : ""}>
            <CardContent className="p-5">
                <div className={`w-10 h-10 rounded-lg grid place-items-center ${accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="overline text-brand-stone mt-4">{label}</div>
                <div className="font-serif text-3xl mt-1 text-brand-ink">{value}</div>
            </CardContent>
        </Card>
    );
}

function SectionHeader({ title, children }) {
    return (
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <div>
                <div className="overline text-brand-stone">Admin</div>
                <h2 className="font-serif text-3xl text-brand-ink">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function ClassesTab() {
    const [classes, setClasses] = React.useState([]);
    const [teachers, setTeachers] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null); // class being edited, or null for create
    const emptyForm = {
        name: "", grade: "PreK", teacher_ids: [], room: "", schedule: "Sundays 10:00 AM - 1:00 PM",
        capacity: 20, description: "", tuition_cents: 15000, gender: null,
    };
    const [form, setForm] = React.useState(emptyForm);
    const [aiTopics, setAiTopics] = React.useState("");
    const [generating, setGenerating] = React.useState(false);
    const { t } = useI18n();

    const GENDER_SPLIT = ["6", "7", "8", "9", "10", "11", "12"];
    const needsGender = GENDER_SPLIT.includes(form.grade);

    const load = React.useCallback(() => {
        api.get("/classes").then(r => setClasses(r.data));
        api.get("/admin/users", { params: { role: "teacher" } }).then(r => setTeachers(r.data));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    function openCreate() {
        setEditing(null); setForm(emptyForm); setOpen(true);
    }
    function openEdit(c) {
        setEditing(c);
        setForm({
            name: c.name, grade: c.grade,
            // Prefer the new multi-teacher array, fall back to the legacy single id.
            teacher_ids: c.teacher_ids?.length ? c.teacher_ids : (c.teacher_id ? [c.teacher_id] : []),
            room: c.room || "", schedule: c.schedule || "Sundays 10:00 AM - 1:00 PM",
            capacity: c.capacity || 20, description: c.description || "",
            tuition_cents: c.tuition_cents || 15000,
            gender: c.gender || null,
        });
        setOpen(true);
    }

    async function generate() {
        setGenerating(true);
        try {
            const { data } = await api.post("/ai/course-description", {
                class_name: form.name, grade: form.grade, key_topics: aiTopics,
            });
            setForm({ ...form, description: data.description });
            toast.success("Description generated");
        } catch (err) { toast.error("AI generation failed"); }
        finally { setGenerating(false); }
    }

    async function save() {
        // Client-side validation mirroring the backend policy.
        if (needsGender && (form.gender !== "male" && form.gender !== "female")) {
            return toast.error("Grades 6-12 must be Boys or Girls");
        }
        if (!needsGender && form.gender) {
            return toast.error("Only grades 6-12 can be gender-specific");
        }
        const payload = { ...form, teacher_ids: form.teacher_ids || [], gender: form.gender || null };
        try {
            if (editing) {
                await api.put(`/classes/${editing.id}`, payload);
                toast.success("Class updated");
            } else {
                await api.post("/classes", payload);
                toast.success("Class created");
            }
            setOpen(false);
            setForm(emptyForm);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        }
    }

    return (
        <div>
            <SectionHeader title={t("admin_classes")}>
                <Button data-testid={ADMIN.createClassBtn} onClick={openCreate} className="bg-primary hover:bg-primary/90 gap-2">
                    <Plus className="w-4 h-4" /> {t("new_class")}
                </Button>
            </SectionHeader>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">
                            {editing ? t("edit_class_title") : t("create_class_title")}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div><Label>{t("class_name")}</Label><Input data-testid="class-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>{t("grade")}</Label>
                                <Select value={form.grade} onValueChange={v => {
                                    // If moving into a mixed grade, clear gender; if moving into a 6-12 grade, default to Boys.
                                    const nextIsSplit = GENDER_SPLIT.includes(v);
                                    setForm({ ...form, grade: v, gender: nextIsSplit ? (form.gender || "male") : null });
                                }}>
                                    <SelectTrigger data-testid="class-grade-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>{GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t("gender")}</Label>
                                <Select
                                    value={form.gender ?? "none"}
                                    onValueChange={v => setForm({ ...form, gender: v === "none" ? null : v })}>
                                    <SelectTrigger data-testid="class-gender-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male" data-testid="gender-boys">{t("boys")}</SelectItem>
                                        <SelectItem value="female" data-testid="gender-girls">{t("girls")}</SelectItem>
                                        <SelectItem value="none" disabled={needsGender} data-testid="gender-mixed">{t("mixed")}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <div className="text-[11px] text-brand-stone mt-1 italic">
                                    {needsGender ? "Grades 6-12 must be Boys or Girls." : "Pre-K through 5 stay mixed."}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 sm:col-span-1">
                                <Label>{t("teacher")}</Label>
                                <div className="mt-1.5 rounded-md border border-input bg-background p-2 min-h-[40px] flex flex-wrap gap-1.5" data-testid="class-teacher-chips">
                                    {form.teacher_ids.length === 0 && (
                                        <span className="text-xs text-brand-stone self-center px-1">No teachers assigned</span>
                                    )}
                                    {form.teacher_ids.map(tid => {
                                        const tc = teachers.find(x => x.id === tid);
                                        return (
                                            <span key={tid} className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-xs font-medium">
                                                {tc?.full_name || "Unknown"}
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, teacher_ids: form.teacher_ids.filter(x => x !== tid) })}
                                                    data-testid={`remove-teacher-${tid}`}
                                                    className="ml-0.5 text-primary/70 hover:text-primary"
                                                    aria-label="Remove teacher"
                                                >×</button>
                                            </span>
                                        );
                                    })}
                                </div>
                                <Select
                                    value=""
                                    onValueChange={v => {
                                        if (!v) return;
                                        if (form.teacher_ids.includes(v)) return;
                                        setForm({ ...form, teacher_ids: [...form.teacher_ids, v] });
                                    }}
                                >
                                    <SelectTrigger className="mt-2" data-testid="add-teacher-select"><SelectValue placeholder="+ Add a teacher" /></SelectTrigger>
                                    <SelectContent>
                                        {teachers
                                            .filter(tc => !form.teacher_ids.includes(tc.id))
                                            .map(tc => <SelectItem key={tc.id} value={tc.id}>{tc.full_name}</SelectItem>)}
                                        {teachers.every(tc => form.teacher_ids.includes(tc.id)) && (
                                            <div className="px-2 py-1.5 text-xs text-brand-stone">All teachers added</div>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>{t("room")}</Label><Input value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div><Label>{t("capacity")}</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) })} className="mt-1.5" /></div>
                        <div>
                            <div className="flex justify-between items-end">
                                <Label>{t("description")}</Label>
                                <Button size="sm" variant="outline" onClick={generate} disabled={generating || !form.name}
                                    data-testid={ADMIN.aiGenerateBtn} className="gap-2">
                                    <Sparkles className="w-3 h-3" /> {generating ? t("generating") : t("ai_generate")}
                                </Button>
                            </div>
                            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[100px]" />
                            <Input placeholder="Key topics for AI (optional)" value={aiTopics} onChange={e => setAiTopics(e.target.value)} className="mt-2 text-xs" />
                        </div>
                        <Button data-testid="save-class-btn" className="w-full bg-primary hover:bg-primary/90" onClick={save}>
                            {t("save_class")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="grid md:grid-cols-2 gap-4">
                {classes.map(c => (
                    <Card key={c.id}><CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Badge className="bg-primary/10 text-primary border-0">{c.grade}</Badge>
                                    {c.gender && (
                                        <Badge className={c.gender === "male" ? "bg-blue-100 text-blue-800 border-0" : "bg-pink-100 text-pink-800 border-0"}>
                                            {c.gender === "male" ? t("boys") : t("girls")}
                                        </Badge>
                                    )}
                                </div>
                                <h3 className="font-dash text-lg font-semibold mt-2">{c.name}</h3>
                                <div className="text-xs text-brand-stone mt-1">
                                    {c.teachers?.length
                                        ? c.teachers.map(t => t.full_name).join(", ")
                                        : (c.teacher_name || "No teacher")}
                                    {c.room ? ` · ${c.room}` : ""}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button data-testid={`edit-class-${c.id}`} onClick={() => openEdit(c)} className="text-brand-stone hover:text-primary" title={t("edit")}>
                                    <span className="text-sm underline">{t("edit")}</span>
                                </button>
                                <button onClick={async () => { if (window.confirm("Delete this class?")) { await api.delete(`/classes/${c.id}`); load(); } }} className="text-brand-stone hover:text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        {c.description && <p className="text-sm text-brand-stone mt-3 line-clamp-3">{c.description}</p>}
                    </CardContent></Card>
                ))}
            </div>
        </div>
    );
}

function UsersTab() {
    const [users, setUsers] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({ full_name: "", email: "", phone: "", password: "" });
    const load = React.useCallback(() => api.get("/admin/users").then(r => setUsers(r.data)), []);
    React.useEffect(() => { load(); }, [load]);
    async function createTeacher() {
        try {
            await api.post("/admin/teachers", form);
            toast.success("Teacher created"); setOpen(false);
            setForm({ full_name: "", email: "", phone: "", password: "" }); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }
    return (
        <div>
            <SectionHeader title="Users">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={ADMIN.createTeacherBtn} className="bg-primary hover:bg-primary/90 gap-2"><Plus className="w-4 h-4" /> Add teacher</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-serif text-2xl">Create teacher account</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label>Full name</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Password</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>
                            <Button className="w-full bg-primary hover:bg-primary/90" onClick={createTeacher}>Create</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </SectionHeader>
            <Card><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/50">
                        <tr className="text-left">
                            <th className="p-4 font-medium">Name</th>
                            <th className="p-4 font-medium">Email</th>
                            <th className="p-4 font-medium">Role</th>
                            <th className="p-4 font-medium">Phone</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} className="border-t border-border/60">
                                <td className="p-4">{u.full_name}</td>
                                <td className="p-4 text-brand-stone">{u.email}</td>
                                <td className="p-4"><Badge className="bg-primary/10 text-primary border-0 capitalize">{u.role}</Badge></td>
                                <td className="p-4 text-brand-stone">{u.phone || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardContent></Card>
        </div>
    );
}

function PaymentsTab() {
    const [regs, setRegs] = React.useState([]);
    const [editing, setEditing] = React.useState(null);
    const [retroOnly, setRetroOnly] = React.useState(false);
    const load = React.useCallback(() => api.get("/admin/registrations").then(r => setRegs(r.data)), []);
    React.useEffect(() => { load(); }, [load]);
    async function markPaid(id, method) {
        await api.post(`/admin/registrations/${id}/mark-paid`, { method });
        toast.success(method === "in_person" ? "Marked paid in person" : "Marked paid");
        load();
    }
    const retroCount = React.useMemo(() => regs.filter(r => r.retro_join_date).length, [regs]);
    const visible = retroOnly ? regs.filter(r => r.retro_join_date) : regs;
    return (
        <div>
            <SectionHeader title="Payments">
                <NewInvoiceDialog onCreated={load} />
            </SectionHeader>
            <PaymentReminders />
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <button
                    type="button"
                    data-testid="filter-retro-only-btn"
                    onClick={() => setRetroOnly(v => !v)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3.5 h-9 text-xs font-medium transition ${
                        retroOnly
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-white text-primary border-primary/30 hover:bg-primary/5"
                    }`}
                >
                    <History className="w-3.5 h-3.5" />
                    {retroOnly ? "Showing backdated only" : "Filter: backdated only"}
                    <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${retroOnly ? "bg-white/20" : "bg-primary/10"}`}>{retroCount}</span>
                </button>
                {retroOnly && (
                    <span className="text-xs text-brand-stone">
                        Backdated invoices are those a parent flagged as starting on an earlier Sunday during registration.
                    </span>
                )}
            </div>
            <div className="space-y-3">
                {visible.map(r => (
                    <Card key={r.id} className={
                        r.retro_join_date ? "border-primary/40 bg-primary/5"
                        : (r.in_person_requested && r.status !== "PAID" ? "border-amber-300 bg-amber-50/40" : "")
                    }><CardContent className="p-5 flex justify-between items-center flex-wrap gap-4">
                        <div className="min-w-0">
                            <div className="font-dash text-base font-semibold">{r.parent_name}</div>
                            <div className="text-xs text-brand-stone">{r.parent_email} · {new Date(r.created_at).toLocaleDateString()}</div>
                            <div className="text-xs mt-1">{r.students.map(s => `${s.first_name} (Grade ${s.grade})`).join(", ")}</div>
                            {r.retro_join_date && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-primary text-primary-foreground rounded-full px-2.5 py-1 font-medium" data-testid={`retro-badge-${r.id}`}>
                                    <History className="w-3 h-3" />
                                    Backdated to {new Date(r.retro_join_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                </div>
                            )}
                            {r.in_person_requested && r.status !== "PAID" && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-amber-200 text-amber-900 rounded-full px-2.5 py-1 font-medium" data-testid={`ip-requested-${r.id}`}>
                                    <HandCoins className="w-3 h-3" />
                                    Parent will pay in person
                                    {r.in_person_requested_at && (
                                        <span className="opacity-75">· requested {new Date(r.in_person_requested_at).toLocaleDateString()}</span>
                                    )}
                                </div>
                            )}
                            {r.description && (
                                <div className="text-[11px] mt-1 text-brand-stone">{r.description}</div>
                            )}
                            {r.status === "PAID" && r.payment_method && (
                                <div className="text-[11px] mt-1 text-brand-stone italic">
                                    Paid via {r.payment_method === "in_person" ? "cash / in person" : r.payment_method === "online" ? "Square" : "admin"}
                                    {r.paid_at ? ` · ${new Date(r.paid_at).toLocaleDateString()}` : ""}
                                </div>
                            )}
                            {r.edit_history?.length > 0 && (
                                <div className="text-[11px] mt-1 text-amber-700 italic">
                                    Edited {r.edit_history.length}× — last by {r.edit_history.at(-1).by_name}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right">
                                <div className="font-serif text-xl">${(r.amount_cents / 100).toFixed(2)}</div>
                                <Badge className={r.status === "PAID" ? "bg-emerald-100 text-emerald-800 border-0" : "bg-amber-100 text-amber-800 border-0"}>{r.status}</Badge>
                            </div>
                            {r.status !== "PAID" && (
                                <div className="flex flex-col gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => setEditing(r)}
                                        data-testid={`edit-invoice-${r.id}`}
                                        className="gap-1.5">
                                        <Pencil className="w-3.5 h-3.5" /> Edit
                                    </Button>
                                    <Button size="sm" onClick={() => markPaid(r.id, "in_person")}
                                        data-testid="mark-paid-in-person-btn"
                                        className="bg-primary hover:bg-primary/90 gap-1.5">
                                        <HandCoins className="w-3.5 h-3.5" /> Paid in person
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => markPaid(r.id, "manual")}
                                        data-testid={ADMIN.markPaidBtn}>
                                        Mark paid
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent></Card>
                ))}
                {visible.length === 0 && (
                    <div className="text-center py-16 text-brand-stone">
                        {retroOnly ? "No backdated registrations yet." : "No registrations yet."}
                    </div>
                )}
            </div>
            <EditInvoiceDialog reg={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
    );
}

function EditInvoiceDialog({ reg, onClose, onSaved }) {
    const [amount, setAmount] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [note, setNote] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    React.useEffect(() => {
        if (reg) {
            setAmount(((reg.amount_cents || 0) / 100).toFixed(2));
            setDescription(reg.description || "");
            setNote("");
        }
    }, [reg]);
    async function save() {
        if (!reg) return;
        const cents = Math.round(parseFloat(amount || "0") * 100);
        if (!Number.isFinite(cents) || cents < 0) return toast.error("Amount must be $0 or more");
        setBusy(true);
        try {
            await api.patch(`/admin/registrations/${reg.id}`, {
                amount_cents: cents,
                description,
                note: note || null,
            });
            toast.success("Invoice updated");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally { setBusy(false); }
    }
    return (
        <Dialog open={!!reg} onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">Edit invoice</DialogTitle></DialogHeader>
                {reg && (
                    <div className="space-y-3">
                        <div className="text-xs text-brand-stone">For {reg.parent_name} · {reg.parent_email}</div>
                        <div>
                            <Label>Amount (USD)</Label>
                            <Input data-testid="edit-invoice-amount" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea data-testid="edit-invoice-description" value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Audit note (optional)</Label>
                            <Input data-testid="edit-invoice-note" placeholder="e.g. Discount applied — sibling scholarship" value={note} onChange={e => setNote(e.target.value)} className="mt-1.5" />
                        </div>
                        <Button data-testid="save-invoice-edit-btn" onClick={save} disabled={busy} className="w-full bg-primary hover:bg-primary/90">
                            {busy ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function NewInvoiceDialog({ onCreated }) {
    const [open, setOpen] = React.useState(false);
    const [mode, setMode] = React.useState("existing"); // "existing" | "new"
    const [parents, setParents] = React.useState([]);
    const [selectedFamily, setSelectedFamily] = React.useState("");
    const [selectedKids, setSelectedKids] = React.useState({});
    const [newParent, setNewParent] = React.useState({ full_name: "", email: "", phone: "" });
    const [newKids, setNewKids] = React.useState([{ first_name: "", last_name: "", date_of_birth: "", gender: "" }]);
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        if (open && mode === "existing" && parents.length === 0) {
            api.get("/admin/parents").then(r => setParents(r.data));
        }
    }, [open, mode, parents.length]);

    const selectedParent = parents.find(p => p.family_id === selectedFamily);

    async function submit() {
        setBusy(true);
        try {
            let payload;
            if (mode === "existing") {
                const student_ids = Object.entries(selectedKids).filter(([, v]) => v).map(([k]) => k);
                if (!selectedFamily || student_ids.length === 0) {
                    toast.error("Pick a parent and at least one child");
                    return;
                }
                payload = { mode: "existing", family_id: selectedFamily, student_ids };
            } else {
                if (!newParent.full_name || !newParent.email) {
                    toast.error("Parent name and email are required");
                    return;
                }
                const kids = newKids.filter(k => k.first_name && k.last_name && k.date_of_birth);
                if (kids.length === 0) {
                    toast.error("Add at least one child (first, last name, DOB)");
                    return;
                }
                payload = {
                    mode: "new",
                    parent_full_name: newParent.full_name,
                    parent_email: newParent.email,
                    parent_phone: newParent.phone,
                    new_students: kids.map(k => ({ ...k, gender: k.gender || undefined })),
                };
            }
            const { data } = await api.post("/admin/invoices", payload);
            toast.success(`Invoice created · $${(data.amount_cents / 100).toFixed(2)}`);
            setOpen(false);
            setSelectedFamily(""); setSelectedKids({}); setNewParent({ full_name: "", email: "", phone: "" });
            setNewKids([{ first_name: "", last_name: "", date_of_birth: "", gender: "" }]);
            onCreated?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create invoice");
        } finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="admin-new-invoice-btn" className="bg-primary hover:bg-primary/90 gap-2">
                    <Plus className="w-4 h-4" /> New invoice
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Create invoice</DialogTitle></DialogHeader>
                <div className="flex gap-2 mb-4">
                    <Button size="sm" variant={mode === "existing" ? "default" : "outline"} onClick={() => setMode("existing")}
                        data-testid="invoice-mode-existing" className={mode === "existing" ? "bg-primary" : ""}>Existing family</Button>
                    <Button size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")}
                        data-testid="invoice-mode-new" className={mode === "new" ? "bg-primary" : ""}>New parent</Button>
                </div>
                {mode === "existing" ? (
                    <div className="space-y-4">
                        <div>
                            <Label>Parent</Label>
                            <Select value={selectedFamily} onValueChange={v => { setSelectedFamily(v); setSelectedKids({}); }}>
                                <SelectTrigger data-testid="invoice-parent-select" className="mt-1.5"><SelectValue placeholder="Choose parent…" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {parents.filter(p => p.family_id).map(p => (
                                        <SelectItem key={p.family_id} value={p.family_id}>
                                            {p.full_name} · {p.email} · {p.child_count} {p.child_count === 1 ? "child" : "kids"}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedParent && (
                            <div>
                                <Label>Children</Label>
                                <div className="mt-1.5 grid gap-2">
                                    {selectedParent.children.map(c => (
                                        <label key={c.id} className="flex items-center gap-3 border border-border rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40">
                                            <input
                                                type="checkbox"
                                                data-testid={`invoice-kid-${c.id}`}
                                                checked={!!selectedKids[c.id]}
                                                onChange={e => setSelectedKids({ ...selectedKids, [c.id]: e.target.checked })}
                                            />
                                            <span>{c.first_name} {c.last_name}</span>
                                            <Badge variant="secondary" className="ml-auto">Grade {c.grade}</Badge>
                                        </label>
                                    ))}
                                </div>
                                <div className="mt-2 text-xs text-brand-stone">
                                    Total: <b>${(Object.values(selectedKids).filter(Boolean).length * 75).toFixed(2)}</b>
                                    <span className="italic"> ($75/child · $15 registration + $60 first month)</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Parent name *</Label><Input data-testid="new-parent-name" value={newParent.full_name} onChange={e => setNewParent({ ...newParent, full_name: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Email *</Label><Input data-testid="new-parent-email" type="email" value={newParent.email} onChange={e => setNewParent({ ...newParent, email: e.target.value })} className="mt-1.5" /></div>
                            <div className="col-span-2"><Label>Phone</Label><Input value={newParent.phone} onChange={e => setNewParent({ ...newParent, phone: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Children *</Label>
                                <Button size="sm" variant="outline" onClick={() => setNewKids([...newKids, { first_name: "", last_name: "", date_of_birth: "", gender: "" }])} data-testid="new-invoice-add-kid-btn" className="h-7 gap-1"><Plus className="w-3 h-3" /> Add child</Button>
                            </div>
                            <div className="space-y-2">
                                {newKids.map((k, i) => (
                                    <div key={i} className="grid grid-cols-4 gap-2 items-end">
                                        <Input data-testid={`new-child-${i}-first-name`} placeholder="First name" value={k.first_name} onChange={e => { const c = [...newKids]; c[i].first_name = e.target.value; setNewKids(c); }} />
                                        <Input data-testid={`new-child-${i}-last-name`} placeholder="Last name" value={k.last_name} onChange={e => { const c = [...newKids]; c[i].last_name = e.target.value; setNewKids(c); }} />
                                        <Input data-testid={`new-child-${i}-dob`} type="date" value={k.date_of_birth} onChange={e => { const c = [...newKids]; c[i].date_of_birth = e.target.value; setNewKids(c); }} />
                                        <Select value={k.gender} onValueChange={v => { const c = [...newKids]; c[i].gender = v; setNewKids(c); }}>
                                            <SelectTrigger data-testid={`new-child-${i}-gender`}><SelectValue placeholder="Gender" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="male">Male</SelectItem>
                                                <SelectItem value="female">Female</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 text-xs text-brand-stone">
                                Total: <b>${(newKids.filter(k => k.first_name && k.last_name && k.date_of_birth).length * 75).toFixed(2)}</b>
                            </div>
                        </div>
                    </div>
                )}
                <Button onClick={submit} disabled={busy} data-testid="submit-new-invoice-btn" className="mt-4 w-full bg-primary hover:bg-primary/90">
                    {busy ? "Creating…" : "Create invoice"}
                </Button>
            </DialogContent>
        </Dialog>
    );
}

function ParentsDirectoryTab() {
    const [rows, setRows] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [err, setErr] = React.useState(null);
    const load = React.useCallback(() => {
        api.get("/admin/parents")
            .then(r => { setRows(r.data); setErr(null); })
            .catch(e => setErr(e.response?.data?.detail || e.message || "Failed to load parents"));
    }, []);
    React.useEffect(() => { load(); }, [load]);
    const filtered = rows.filter(r =>
        !q || `${r.full_name} ${r.email} ${r.phone}`.toLowerCase().includes(q.toLowerCase()) ||
        r.children.some(c => `${c.first_name} ${c.last_name}`.toLowerCase().includes(q.toLowerCase()))
    );
    return (
        <div>
            <SectionHeader title="Parents directory"><AddParentDialog onDone={load} /></SectionHeader>
            <div className="relative mb-4 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-stone" />
                <Input data-testid="parents-search" placeholder="Search by name, email, phone or child name…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-brand-stone border-b border-border">
                            <th className="p-3">Parent</th>
                            <th className="p-3">Contact</th>
                            <th className="p-3">Children</th>
                            <th className="p-3 text-right">Total paid</th>
                            <th className="p-3">Last paid</th>
                        </tr>
                    </thead>
                    <tbody data-testid="parents-directory-body">
                        {filtered.map(r => (
                            <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/40">
                                <td className="p-3 font-medium">{r.full_name}</td>
                                <td className="p-3 text-xs">
                                    <div>{r.email}</div>
                                    <div className="text-brand-stone">{r.phone || "—"}</div>
                                </td>
                                <td className="p-3">
                                    <div className="flex flex-wrap gap-1">
                                        {r.children.length === 0 && <span className="text-brand-stone italic">no kids</span>}
                                        {r.children.map(c => (
                                            <Badge key={c.id} variant="secondary" className="text-xs">
                                                {c.first_name} {c.last_name} · G{c.grade}
                                            </Badge>
                                        ))}
                                    </div>
                                </td>
                                <td className="p-3 text-right font-dash">${(r.total_paid_cents / 100).toFixed(2)}</td>
                                <td className="p-3 text-xs">{r.last_paid_at ? new Date(r.last_paid_at).toLocaleDateString() : "—"}</td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={5} className="p-10 text-center text-brand-stone">
                                {err ? <span className="text-destructive">{err}</span>
                                     : rows.length === 0 ? "No parents have signed up yet."
                                     : "No parents match your search."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </CardContent></Card>
        </div>
    );
}

function StudentsDirectoryTab() {
    const [rows, setRows] = React.useState([]);
    const [classes, setClasses] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [view, setView] = React.useState("all");
    const load = React.useCallback(() => {
        api.get("/admin/students").then(r => setRows(r.data));
        api.get("/classes").then(r => setClasses([...r.data].sort((a, b) =>
            GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade) || a.name.localeCompare(b.name))));
    }, []);
    React.useEffect(() => { load(); }, [load]);
    const unassigned = rows.filter(r => !r.class_id).length;
    const dupes = rows.filter(r => r.is_duplicate).length;
    const filtered = rows.filter(r =>
        (view === "all" || (view === "unassigned" ? !r.class_id : r.is_duplicate)) && (!q ||
        `${r.first_name} ${r.last_name} ${r.parent_name || ""} ${r.parent_email || ""} ${r.grade || ""}`.toLowerCase().includes(q.toLowerCase()))
    );
    async function autoAssign() {
        const { data } = await api.post("/admin/students/auto-assign");
        toast.success(data.placed ? `Placed ${data.placed} student${data.placed === 1 ? "" : "s"} into classes` : "Everyone already has a class");
        load();
    }
    async function remove(r) {
        if (!window.confirm(`Remove ${r.first_name} ${r.last_name} (Grade ${r.grade}) permanently? Their class placement and attendance are removed too. Paid invoices are kept.`)) return;
        try {
            await api.delete(`/admin/students/${r.id}`);
            toast.success(`${r.first_name} ${r.last_name} removed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    }
    return (
        <div>
            <SectionHeader title="Students directory">
                <div className="flex flex-wrap gap-2">
                    {unassigned > 0 && <Button variant="outline" onClick={autoAssign} className="gap-1.5" data-testid="auto-assign-btn"><Shuffle className="w-4 h-4" /> Auto-assign {unassigned} unassigned</Button>}
                    <AddStudentDialog onDone={load} />
                </div>
            </SectionHeader>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative max-w-md flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-stone" />
                    <Input data-testid="students-search" placeholder="Search by student, grade or parent…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
                </div>
                <div className="flex gap-1">
                    {[["all", `All (${rows.length})`], ["unassigned", `Unassigned (${unassigned})`], ["duplicates", `Duplicates (${dupes})`]].map(([k, label]) => (
                        <Button key={k} size="sm" variant={view === k ? "default" : "outline"} onClick={() => setView(k)} data-testid={`students-filter-${k}`}>{label}</Button>
                    ))}
                </div>
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-brand-stone border-b border-border">
                            <th className="p-3">Student</th>
                            <th className="p-3">Grade</th>
                            <th className="p-3">Class</th>
                            <th className="p-3">Parent</th>
                            <th className="p-3">Parent contact</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody data-testid="students-directory-body">
                        {filtered.map(r => (
                            <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`student-row-${r.id}`}>
                                <td className="p-3 font-medium">
                                    {r.first_name} {r.last_name}
                                    {r.is_duplicate && <Badge className="ml-2 bg-amber-100 text-amber-800 border-0 text-[10px]" data-testid={`duplicate-badge-${r.id}`}>Duplicate</Badge>}
                                    {r.is_duplicate && r.created_at && <div className="text-[11px] text-brand-stone font-normal">added {new Date(r.created_at).toLocaleDateString()}</div>}
                                </td>
                                <td className="p-3"><Badge variant="secondary">G{r.grade}</Badge></td>
                                <td className="p-3 text-xs"><ClassAssignSelect student={r} classes={classes} onDone={load} /></td>
                                <td className="p-3 text-xs">{r.parent_name || "—"}</td>
                                <td className="p-3 text-xs">
                                    <div>{r.parent_email || "—"}</div>
                                    <div className="text-brand-stone">{r.parent_phone || "—"}</div>
                                </td>
                                <td className="p-3 text-right">
                                    <Button variant="ghost" size="sm" onClick={() => remove(r)} className="text-destructive hover:text-destructive" data-testid={`remove-student-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={6} className="p-10 text-center text-brand-stone">No students match.</td></tr>
                        )}
                    </tbody>
                </table>
            </CardContent></Card>
        </div>
    );
}

function AnnouncementsTab() {
    const { t } = useI18n();
    const [items, setItems] = React.useState([]);
    const [form, setForm] = React.useState({ title: "", body: "", audience: "all", attachments: [] });
    const [open, setOpen] = React.useState(false);
    const load = React.useCallback(() => api.get("/announcements").then(r => setItems(r.data)), []);
    React.useEffect(() => { load(); }, [load]);
    async function save() {
        if (!form.title.trim() || !form.body.trim()) return toast.error(t("volunteers_name_required"));
        await api.post("/announcements", form);
        toast.success(t("announcement_publish"));
        setForm({ title: "", body: "", audience: "all", attachments: [] }); setOpen(false); load();
    }
    return (
        <div>
            <SectionHeader title={t("admin_announcements")}>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={ADMIN.createAnnouncementBtn} className="bg-primary hover:bg-primary/90 gap-2"><Plus className="w-4 h-4" /> {t("announcement_new")}</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">{t("announcement_new")}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label>{t("announcement_title_field")}</Label><Input data-testid="announcement-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>{t("announcement_body_field")}</Label><Textarea data-testid="announcement-body-input" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className="mt-1.5 min-h-[120px]" /></div>
                            <div><Label>{t("announcement_audience")}</Label>
                                <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                                    <SelectTrigger data-testid="announcement-audience-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all" data-testid="audience-all">{t("audience_everyone")}</SelectItem>
                                        <SelectItem value="parents" data-testid="audience-parents">{t("audience_parents")}</SelectItem>
                                        <SelectItem value="teachers" data-testid="audience-teachers">{t("audience_teachers")}</SelectItem>
                                        <SelectItem value="teachers_parents" data-testid="audience-teachers-parents">{t("audience_teachers_parents")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t("homework_attachments")}</Label>
                                <div className="mt-1.5">
                                    <AttachmentPicker value={form.attachments} onChange={v => setForm({ ...form, attachments: v })} testid="announcement-attachments" />
                                </div>
                            </div>
                            <Button data-testid="publish-announcement-btn" onClick={save} className="w-full bg-primary hover:bg-primary/90">{t("announcement_publish")}</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </SectionHeader>
            <div className="space-y-3">
                {items.map(a => (
                    <Card key={a.id}><CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Megaphone className="w-4 h-4 text-primary" />
                                <div className="overline text-brand-stone">{audienceLabel(a.audience, t)}</div>
                            </div>
                            <button onClick={async () => { if (window.confirm(t("delete") + "?")) { await api.delete(`/announcements/${a.id}`); load(); } }} className="text-brand-stone hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="font-dash text-lg font-semibold mt-2">{a.title}</div>
                        <p className="text-brand-stone mt-1 whitespace-pre-wrap">{a.body}</p>
                        <AttachmentList items={a.attachments} testid={`ann-att-${a.id}`} />
                    </CardContent></Card>
                ))}
            </div>
        </div>
    );
}

function audienceLabel(a, t) {
    if (a === "teachers_parents") return t("audience_teachers_parents_short");
    if (a === "parents") return t("audience_parents");
    if (a === "teachers") return t("audience_teachers");
    return t("audience_everyone");
}

function EventsTab() {
    const [events, setEvents] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({ title: "", date: "", description: "", category: "school" });
    const load = React.useCallback(() => api.get("/events").then(r => setEvents(r.data)), []);
    React.useEffect(() => { load(); }, [load]);
    async function save() {
        await api.post("/events", form);
        toast.success("Event added");
        setForm({ title: "", date: "", description: "", category: "school" }); setOpen(false); load();
    }
    return (
        <div>
            <SectionHeader title="Events">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={ADMIN.createEventBtn} className="bg-primary hover:bg-primary/90 gap-2"><Plus className="w-4 h-4" /> New event</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-serif text-2xl">Add calendar event</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Category</Label>
                                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="school">School</SelectItem>
                                        <SelectItem value="holiday">Holiday</SelectItem>
                                        <SelectItem value="ramadan">Ramadan</SelectItem>
                                        <SelectItem value="eid">Eid</SelectItem>
                                        <SelectItem value="community">Community</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
                            <Button onClick={save} className="w-full bg-primary hover:bg-primary/90">Add event</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </SectionHeader>
            <div className="grid md:grid-cols-2 gap-3">
                {events.map(e => (
                    <Card key={e.id}><CardContent className="p-5 flex items-start justify-between">
                        <div>
                            <Badge className="bg-primary/10 text-primary border-0 capitalize">{e.category}</Badge>
                            <div className="font-dash text-lg font-semibold mt-2">{e.title}</div>
                            <div className="text-xs text-brand-stone">{e.date}</div>
                            {e.description && <p className="text-sm text-brand-stone mt-2">{e.description}</p>}
                        </div>
                        <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/events/${e.id}`); load(); } }} className="text-brand-stone hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </CardContent></Card>
                ))}
            </div>
        </div>
    );
}
